import React, { useState, useEffect, useRef } from 'react';
import { ImagePlus, Trash2, Edit2, Star, Plus, CheckCircle, AlertCircle, Sparkles, Image, GripVertical } from 'lucide-react';
import axios from 'axios';
import { getAuthHeaders } from '../../context/AuthContext';
import Modal from '../../components/Modal';

const API = process.env.REACT_APP_BACKEND_URL;
const MAX_IMAGES = 15;

function Toast({ message, type, onClose }) {
  useEffect(() => { const t = setTimeout(onClose, 3000); return () => clearTimeout(t); }, [onClose]);
  return (
    <div className={`fixed bottom-6 right-6 z-50 flex items-center gap-3 px-5 py-3.5 rounded-2xl shadow-lg text-sm font-body animate-fade-in-up
      ${type === 'success' ? 'bg-green-50 border border-green-200 text-green-700' : 'bg-red-50 border border-red-200 text-red-700'}`}>
      {type === 'success' ? <CheckCircle className="w-4 h-4 shrink-0" /> : <AlertCircle className="w-4 h-4 shrink-0" />}
      {message}
    </div>
  );
}

function StarPicker({ value, onChange }) {
  return (
    <div className="flex gap-1">
      {[1, 2, 3, 4, 5].map(n => (
        <button key={n} type="button" onClick={() => onChange(n)}
          className="transition-colors focus:outline-none">
          <Star
            className={`w-6 h-6 transition-colors ${n <= value ? 'text-[#F59E0B]' : 'text-[#E7E5E4]'}`}
            fill={n <= value ? '#F59E0B' : 'none'} strokeWidth={1.5}
          />
        </button>
      ))}
    </div>
  );
}

export default function AdminShowcase() {
  const [tab, setTab] = useState('gallery');
  const [images, setImages] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [showTestForm, setShowTestForm] = useState(false);
  const [editTest, setEditTest] = useState(null);
  const [testForm, setTestForm] = useState({ name: '', text: '', rating: 5 });
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState(null);
  const fileRef = useRef(null);

  // Drag-to-reorder state
  const [dragIdx, setDragIdx] = useState(null);
  const [dragOverIdx, setDragOverIdx] = useState(null);

  const fetchAll = async () => {
    try {
      const [imgRes, testRes] = await Promise.all([
        axios.get(`${API}/api/admin/showcase/images`, { headers: getAuthHeaders() }),
        axios.get(`${API}/api/admin/showcase/testimonials`, { headers: getAuthHeaders() }),
      ]);
      setImages(imgRes.data);
      setTestimonials(testRes.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchAll(); }, []);

  // ── Gallery handlers ──────────────────────────────────────────────────────

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    try {
      await axios.post(`${API}/api/admin/showcase/images`, formData, { headers: getAuthHeaders() });
      setToast({ message: 'Image uploaded!', type: 'success' });
      fetchAll();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setToast({ message: typeof detail === 'string' ? detail : 'Upload failed. Max size ~5MB.', type: 'error' });
    } finally { setUploading(false); e.target.value = ''; }
  };

  const handleDeleteImage = async (id) => {
    if (!window.confirm('Delete this image from the gallery?')) return;
    try {
      await axios.delete(`${API}/api/admin/showcase/images/${id}`, { headers: getAuthHeaders() });
      setToast({ message: 'Image deleted.', type: 'success' });
      fetchAll();
    } catch { setToast({ message: 'Could not delete image.', type: 'error' }); }
  };

  // ── Drag-to-reorder handlers ──────────────────────────────────────────────

  const handleDragStart = (e, idx) => {
    setDragIdx(idx);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, idx) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (idx !== dragOverIdx) setDragOverIdx(idx);
  };

  const handleDragLeave = () => setDragOverIdx(null);

  const handleDragEnd = () => { setDragIdx(null); setDragOverIdx(null); };

  const handleDrop = async (e, dropIdx) => {
    e.preventDefault();
    setDragOverIdx(null);
    if (dragIdx === null || dragIdx === dropIdx) { setDragIdx(null); return; }

    // Reorder locally first (optimistic)
    const reordered = [...images];
    const [moved] = reordered.splice(dragIdx, 1);
    reordered.splice(dropIdx, 0, moved);
    setImages(reordered);
    setDragIdx(null);

    // Persist order to backend
    try {
      await axios.put(`${API}/api/admin/showcase/images/reorder`,
        { ids: reordered.map(img => img.id) },
        { headers: getAuthHeaders() }
      );
      setToast({ message: 'Order saved!', type: 'success' });
    } catch {
      setToast({ message: 'Could not save order. Reverting...', type: 'error' });
      fetchAll(); // revert to server order
    }
  };

  // ── Testimonial handlers ──────────────────────────────────────────────────

  const openAdd = () => { setEditTest(null); setTestForm({ name: '', text: '', rating: 5 }); setShowTestForm(true); };
  const openEdit = (t) => { setEditTest(t); setTestForm({ name: t.name, text: t.text, rating: t.rating }); setShowTestForm(true); };

  const handleTestSubmit = async (e) => {
    e.preventDefault();
    if (!testForm.name.trim() || !testForm.text.trim()) {
      setToast({ message: 'Name and review text are required.', type: 'error' }); return;
    }
    setSubmitting(true);
    try {
      if (editTest) {
        await axios.put(`${API}/api/admin/showcase/testimonials/${editTest.id}`, testForm, { headers: getAuthHeaders() });
        setToast({ message: 'Testimonial updated!', type: 'success' });
      } else {
        await axios.post(`${API}/api/admin/showcase/testimonials`, testForm, { headers: getAuthHeaders() });
        setToast({ message: 'Testimonial added!', type: 'success' });
      }
      setShowTestForm(false);
      fetchAll();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setToast({ message: typeof detail === 'string' ? detail : 'Something went wrong.', type: 'error' });
    } finally { setSubmitting(false); }
  };

  const handleDeleteTest = async (id) => {
    if (!window.confirm('Delete this testimonial?')) return;
    try {
      await axios.delete(`${API}/api/admin/showcase/testimonials/${id}`, { headers: getAuthHeaders() });
      setToast({ message: 'Testimonial deleted.', type: 'success' });
      fetchAll();
    } catch { setToast({ message: 'Could not delete.', type: 'error' }); }
  };

  return (
    <div className="space-y-6 animate-fade-in-up">
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
      <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp"
        className="hidden" onChange={handleFileChange} />

      {/* Header */}
      <div>
        <p className="text-xs font-semibold uppercase tracking-widest text-[#F59E0B] font-body mb-1">Public Facing</p>
        <h1 className="font-heading text-2xl sm:text-3xl font-medium text-[#292524]">Visitor Showcase</h1>
        <p className="text-[#57534E] text-sm font-body mt-1">Manage gallery images and testimonials shown on the public visitor page.</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 bg-white rounded-2xl p-1.5 shadow-card border border-[#E7E5E4]/50 w-fit">
        <button onClick={() => setTab('gallery')} data-testid="showcase-tab-gallery"
          className={`px-5 py-2 rounded-xl text-sm font-heading font-medium transition-all flex items-center gap-2
            ${tab === 'gallery' ? 'bg-[#F59E0B] text-white' : 'text-[#57534E] hover:text-[#D97706]'}`}>
          <Image className="w-4 h-4" strokeWidth={1.5} /> Gallery
        </button>
        <button onClick={() => setTab('testimonials')} data-testid="showcase-tab-testimonials"
          className={`px-5 py-2 rounded-xl text-sm font-heading font-medium transition-all flex items-center gap-2
            ${tab === 'testimonials' ? 'bg-[#F59E0B] text-white' : 'text-[#57534E] hover:text-[#D97706]'}`}>
          <Star className="w-4 h-4" strokeWidth={1.5} /> Testimonials
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><div className="w-10 h-10 border-4 border-[#F59E0B] border-t-transparent rounded-full animate-spin" /></div>
      ) : tab === 'gallery' ? (

        /* ── GALLERY TAB ── */
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-sm text-[#57534E] font-body">
              <span className="font-semibold text-[#292524]">{images.length}</span> / {MAX_IMAGES} images uploaded
              {images.length >= MAX_IMAGES && <span className="ml-2 text-orange-500 text-xs">(limit reached)</span>}
            </p>
            <button
              data-testid="upload-image-button"
              onClick={() => fileRef.current?.click()}
              disabled={uploading || images.length >= MAX_IMAGES}
              className="flex items-center gap-2 bg-[#F59E0B] hover:bg-[#D97706] text-white rounded-full px-5 py-2.5 text-sm font-heading font-medium shadow-button hover:-translate-y-0.5 transition-all disabled:opacity-50">
              <ImagePlus className="w-4 h-4" strokeWidth={1.5} />
              {uploading ? 'Uploading...' : 'Add Image'}
            </button>
          </div>

          {images.length === 0 ? (
            <div className="bg-white rounded-3xl p-12 text-center shadow-card border border-[#E7E5E4]/50">
              <div className="w-16 h-16 bg-[#FFEDD5] rounded-full flex items-center justify-center mx-auto mb-4">
                <Image className="w-8 h-8 text-[#F59E0B]" strokeWidth={1.5} />
              </div>
              <p className="font-heading text-lg font-medium text-[#292524]">No images yet</p>
              <p className="text-[#78716C] text-sm font-body mt-1">Upload photos to show on the visitor gallery. Fallback: default images used.</p>
            </div>
          ) : (
            <>
              <p className="text-xs text-[#A8A29E] font-body flex items-center gap-1.5">
                <GripVertical className="w-3.5 h-3.5" strokeWidth={1.5} />
                Drag images to reorder how they appear on the visitor page
              </p>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
                {images.map((img, idx) => (
                  <div
                    key={img.id}
                    data-testid="gallery-image-card"
                    draggable
                    onDragStart={e => handleDragStart(e, idx)}
                    onDragOver={e => handleDragOver(e, idx)}
                    onDragLeave={handleDragLeave}
                    onDrop={e => handleDrop(e, idx)}
                    onDragEnd={handleDragEnd}
                    className={`relative group aspect-square rounded-2xl overflow-hidden shadow-card border-2 transition-all duration-150 cursor-grab active:cursor-grabbing select-none
                      ${dragIdx === idx ? 'opacity-40 scale-95' : 'opacity-100'}
                      ${dragOverIdx === idx && dragIdx !== idx ? 'border-[#F59E0B] scale-[1.02] shadow-card-hover' : 'border-[#E7E5E4]/50'}
                    `}
                  >
                    <img
                      src={`${API}${img.url}`}
                      alt={`gallery ${idx + 1}`}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300 pointer-events-none"
                      draggable={false}
                    />
                    {/* Drag handle — always visible */}
                    <div className="absolute top-1.5 left-1.5 bg-black/40 rounded-lg p-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <GripVertical className="w-3.5 h-3.5 text-white" strokeWidth={2} />
                    </div>
                    {/* Position badge */}
                    <div className="absolute top-1.5 right-1.5 bg-black/40 text-white text-xs font-heading font-bold rounded-lg px-1.5 py-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                      {idx + 1}
                    </div>
                    {/* Delete button */}
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors duration-200 flex items-end justify-center pb-3">
                      <button
                        data-testid={`delete-image-${img.id}`}
                        onClick={e => { e.stopPropagation(); handleDeleteImage(img.id); }}
                        className="opacity-0 group-hover:opacity-100 transition-opacity bg-red-500 hover:bg-red-600 text-white text-xs font-heading font-medium rounded-full px-3 py-1.5 shadow-lg"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}

          <p className="text-xs text-[#A8A29E] font-body">
            Supported: JPG, PNG, WebP. If gallery has images, the visitor page uses them. Otherwise static fallback is shown.
          </p>
        </div>

      ) : (

        /* ── TESTIMONIALS TAB ── */
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-sm text-[#57534E] font-body">
              <span className="font-semibold text-[#292524]">{testimonials.length}</span> testimonial{testimonials.length !== 1 ? 's' : ''}
            </p>
            <button data-testid="add-testimonial-button" onClick={openAdd}
              className="flex items-center gap-2 bg-[#F59E0B] hover:bg-[#D97706] text-white rounded-full px-5 py-2.5 text-sm font-heading font-medium shadow-button hover:-translate-y-0.5 transition-all">
              <Plus className="w-4 h-4" strokeWidth={2} /> Add Testimonial
            </button>
          </div>

          {testimonials.length === 0 ? (
            <div className="bg-white rounded-3xl p-12 text-center shadow-card border border-[#E7E5E4]/50">
              <div className="w-16 h-16 bg-[#FFEDD5] rounded-full flex items-center justify-center mx-auto mb-4">
                <Star className="w-8 h-8 text-[#F59E0B]" strokeWidth={1.5} />
              </div>
              <p className="font-heading text-lg font-medium text-[#292524]">No testimonials yet</p>
              <p className="text-[#78716C] text-sm font-body mt-1">Add member reviews to show on the visitor page. Fallback: default testimonials used.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {testimonials.map(t => (
                <div key={t.id} data-testid="testimonial-admin-card"
                  className="bg-white rounded-2xl shadow-card border border-[#E7E5E4]/50 p-4 flex flex-col"
                  style={{ height: '200px' }}>
                  {/* Header */}
                  <div className="flex items-start justify-between mb-2 shrink-0">
                    <div>
                      <p className="font-heading font-semibold text-[#292524] text-sm">{t.name}</p>
                      <div className="flex gap-0.5 mt-1">
                        {Array.from({ length: t.rating }).map((_, j) => (
                          <Star key={j} className="w-3 h-3 text-[#F59E0B]" fill="#F59E0B" strokeWidth={0} />
                        ))}
                      </div>
                    </div>
                    <div className="flex gap-1 shrink-0">
                      <button data-testid={`edit-testimonial-${t.id}`} onClick={() => openEdit(t)}
                        className="p-1.5 text-[#A8A29E] hover:text-[#F59E0B] transition-colors rounded-lg hover:bg-[#FFFAF0]">
                        <Edit2 className="w-3.5 h-3.5" strokeWidth={1.5} />
                      </button>
                      <button data-testid={`delete-testimonial-${t.id}`} onClick={() => handleDeleteTest(t.id)}
                        className="p-1.5 text-[#A8A29E] hover:text-red-500 transition-colors rounded-lg hover:bg-red-50">
                        <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} />
                      </button>
                    </div>
                  </div>
                  {/* Scrollable review */}
                  <div className="flex-1 overflow-y-auto" style={{ scrollbarWidth: 'thin', scrollbarColor: '#E7E5E4 transparent' }}>
                    <p className="text-[#57534E] font-body text-xs leading-relaxed italic">"{t.text}"</p>
                  </div>
                </div>
              ))}
            </div>
          )}

          <p className="text-xs text-[#A8A29E] font-body">
            If testimonials exist here, the visitor page uses them. Otherwise static fallback is shown.
          </p>
        </div>
      )}

      {/* Testimonial Form Modal */}
      {showTestForm && (
        <Modal
          title={editTest ? 'Edit Testimonial' : 'Add Testimonial'}
          onClose={() => setShowTestForm(false)}
          footer={
            <div className="flex gap-3">
              <button type="button" onClick={() => setShowTestForm(false)}
                className="flex-1 border-2 border-[#E7E5E4] text-[#57534E] rounded-full py-3 font-heading font-medium text-sm">Cancel</button>
              <button data-testid="submit-testimonial" type="submit" form="test-form" disabled={submitting}
                className="flex-1 bg-[#F59E0B] hover:bg-[#D97706] text-white rounded-full py-3 font-heading font-medium text-sm shadow-button disabled:opacity-60 transition-all">
                {submitting ? 'Saving...' : editTest ? 'Update' : 'Add'}
              </button>
            </div>
          }
        >
          <form id="test-form" onSubmit={handleTestSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-[#57534E] mb-2">Member Name *</label>
              <input type="text" placeholder="e.g. Priya S."
                value={testForm.name} onChange={e => setTestForm(f => ({ ...f, name: e.target.value }))}
                className="bg-white border-2 border-[#E7E5E4] rounded-2xl px-4 py-3 focus:border-[#F59E0B] outline-none w-full text-[#292524] font-body" />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#57534E] mb-2">Rating</label>
              <StarPicker value={testForm.rating} onChange={r => setTestForm(f => ({ ...f, rating: r }))} />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#57534E] mb-2">Review *</label>
              <textarea rows={4} placeholder="What did they say about the class?"
                value={testForm.text} onChange={e => setTestForm(f => ({ ...f, text: e.target.value }))}
                className="bg-white border-2 border-[#E7E5E4] rounded-2xl px-4 py-3 focus:border-[#F59E0B] outline-none w-full text-[#292524] font-body resize-none" />
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}
